import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import useThemeStyles from '../../../hooks/useThemeStyles';

const WritingGoalsCard = ({
  dailyWordGoal,
  onDailyWordGoalChange,
  todayProgress,
  weeklyProgress,
  weeklyTarget,
  weeklyStatus,
  currentStreak,
  longestStreak,
  saving,
  onSave,
}) => {
  const { colors, components } = useThemeStyles();

  const cardStyle = {
    ...components.card,
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };

  return (
    <Box
      id="writing-goals-container"
      sx={{ display: 'flex', flex: 1, minWidth: 0, alignSelf: 'stretch' }}
    >
      <Card
        id="writing-goals-card"
        style={cardStyle}
        sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <CardContent
          id="writing-goals-content"
          sx={{ padding: 3, flex: 1, display: 'flex', flexDirection: 'column' }}
        >
          <Box
            id="writing-goals-header"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Writing Goals
            </Typography>
          </Box>
          <Box
            id="writing-goals-body"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
              flex: 1,
              justifyContent: 'space-between',
            }}
          >
            <Box
              id="writing-goals-stats"
              sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
            >
              <Typography
                id="writing-goals-motivational-text"
                variant="body2"
                sx={{
                  fontStyle: 'italic',
                  fontSize: '0.875rem',
                  color: colors.text.secondary,
                }}
              >
                Consistency over perfection. Small daily steps lead to big achievements.
              </Typography>
              <Box id="progress-bars-container" sx={{ marginTop: 2, marginBottom: 2 }}>
                <Box id="today-progress-container" sx={{ marginBottom: 2 }}>
                  <Box
                    id="today-progress-header"
                    sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1.5 }}
                  >
                    <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                      Today&apos;s progress
                    </Typography>
                    <Typography
                      id="today-progress-text"
                      variant="body2"
                      sx={{ fontWeight: 500, color: colors.text.secondary }}
                    >
                      {todayProgress} / {dailyWordGoal} words
                    </Typography>
                  </Box>
                  <LinearProgress
                    id="today-progress-bar"
                    variant="determinate"
                    value={Math.min(100, (todayProgress / dailyWordGoal) * 100)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: colors.background.secondary,
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
                <Box id="weekly-progress-container">
                  <Box
                    id=", "
                    sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1.5 }}
                  >
                    <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                      Weekly progress
                    </Typography>
                    <Box
                      id="weekly-progress-stats"
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <Typography
                        id="weekly-progress-status"
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: (() => {
                            if (weeklyStatus === 'Ahead') return '#4caf50';
                            if (weeklyStatus === 'Behind') return '#f44336';
                            return colors.text.secondary;
                          })(),
                        }}
                      >
                        {weeklyStatus}
                      </Typography>
                      <Typography
                        id="weekly-progress-text"
                        variant="body2"
                        sx={{ fontWeight: 500, color: colors.text.secondary }}
                      >
                        {weeklyProgress} / {weeklyTarget} words
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    id="weekly-progress-bar"
                    variant="determinate"
                    value={Math.min(100, (weeklyProgress / weeklyTarget) * 100)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: colors.background.secondary,
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        backgroundColor: (() => {
                          if (weeklyStatus === 'Ahead') return '#4caf50';
                          if (weeklyStatus === 'Behind') return '#f44336';
                          return '#1976d2';
                        })(),
                      },
                    }}
                  />
                </Box>
              </Box>
              <Box
                id="streak-container"
                sx={{
                  padding: 2,
                  backgroundColor: colors.background.secondary || '#f5f5f5',
                  borderRadius: '8px',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, marginBottom: 1 }}>
                  Streak
                </Typography>
                <Typography
                  id="current-streak-text"
                  variant="body2"
                  sx={{ marginBottom: 0.5, color: colors.text.secondary }}
                >
                  🔥 Current: {currentStreak} day{currentStreak !== 1 ? 's' : ''}
                </Typography>
                <Typography
                  id="longest-streak-text"
                  variant="body2"
                  sx={{ color: colors.text.secondary }}
                >
                  🏆 Longest: {longestStreak} day{longestStreak !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Box>
            <Box id="writing-goals-form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                id="daily-word-goal-input"
                label="Daily word count goal"
                type="number"
                value={dailyWordGoal}
                onChange={(e) =>
                  onDailyWordGoalChange(Math.max(0, parseInt(e.target.value, 10) || 0))
                }
                fullWidth
                variant="outlined"
                inputProps={{ min: 0 }}
                sx={{
                  '& .MuiInputLabel-root': {
                    color: colors.text.secondary,
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: colors.interactive.primary,
                  },
                  '& .MuiOutlinedInput-input': {
                    color: colors.text.primary,
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: colors.border.subtle,
                    },
                    '&:hover fieldset': {
                      borderColor: colors.interactive.primary,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: colors.interactive.primary,
                    },
                  },
                }}
              />
              <Button
                id="save-goal-button"
                variant="contained"
                onClick={onSave}
                disabled={saving}
                sx={{
                  alignSelf: 'flex-end',
                  textTransform: 'none',
                  borderRadius: '8px',
                  padding: '10px 24px',
                }}
              >
                {saving ? <CircularProgress size={20} /> : 'Save Goal'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

WritingGoalsCard.propTypes = {
  dailyWordGoal: PropTypes.number.isRequired,
  onDailyWordGoalChange: PropTypes.func.isRequired,
  todayProgress: PropTypes.number.isRequired,
  weeklyProgress: PropTypes.number.isRequired,
  weeklyTarget: PropTypes.number.isRequired,
  weeklyStatus: PropTypes.string.isRequired,
  currentStreak: PropTypes.number.isRequired,
  longestStreak: PropTypes.number.isRequired,
  saving: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default WritingGoalsCard;
